import { FC, useEffect, useState } from 'react';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import classNames from 'classnames';
import { Size, useWindowSize } from '../../hook/useWindowSize';
import BusinessButton from '../business-button/business-button';
import { COLORS } from '../business-button/business-button.constants';
import styles from './business-hub-carousel-product.module.scss';

export enum ITEMS_POSITION {
    Left = 'Left',
    Right = 'Right',
}

export enum IMAGE_POSITION {
    Center = 'Center',
    Bottom = 'Bottom',
}

export interface ProductCarouselProps {
    title: string;
    subTitle: string;
    positionItems: ITEMS_POSITION;
    backgroundColor: string;
    colorText: string;
    colorPaginationBulletActive: string;

    items: {
        subTitle: string;
        title: string;
        maxWidthTitle: number;
        description: string;
        image: string;
        heightImageMobile?: number;
        maxWidthImage?: number;
        maxWidthImageMobile?: number;
        imagePosition: IMAGE_POSITION;
        ctaText?: string;
        ctaUrl?: string;
        openLinkInNewTab?: boolean;
    }[];
}

const BusinessHubCarouselProduct: FC<ProductCarouselProps> = (props) => {
    const { title, subTitle, positionItems, items = [], backgroundColor, colorText, colorPaginationBulletActive } = props;
    const options: SwiperOptions = {
        spaceBetween: 10,
        allowTouchMove: true,
        noSwiping: false,
        freeMode: false,
        loop: true,
        effect: 'fade',
        pagination: {
            enabled: true,
            clickable: true,
        },

        speed: 1000,

        autoplay: {
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        },
        slidesPerGroupSkip: 0,

        breakpoints: {
            998: {
                watchOverflow: true,
            },
        },
    };

    const [isMobile, setIsMobile] = useState<boolean>(false);

    const windowSize: Size = useWindowSize();

    const windowWidth: number = windowSize.width || 0;

    useEffect(() => {
        if (windowWidth <= 998) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, [windowWidth]);

    return (
        <div className={styles.container}>
            <div className={styles.wrapperContent}>
                <div className={styles.header}>{title}</div>
                <div className={styles.subHeader}>{subTitle}</div>
                <div
                    className={styles.content}
                    style={{
                        marginTop: subTitle ? '36px' : '44px',
                    }}
                >
                    <Swiper
                        {...options}
                        modules={[Pagination]}
                        style={
                            {
                                ['--swiper-pagination-color']: colorPaginationBulletActive,
                            } as React.CSSProperties
                        }
                    >
                        {items.map((item, index) => {
                            return (
                                <SwiperSlide key={index}>
                                    <div
                                        key={index}
                                        className={classNames(styles.item, {
                                            [styles.left]: positionItems === ITEMS_POSITION.Left,
                                            [styles.right]: positionItems === ITEMS_POSITION.Right,
                                            [styles.center]: item.imagePosition === IMAGE_POSITION.Center,
                                        })}
                                        style={{
                                            backgroundColor: backgroundColor,
                                        }}
                                    >
                                        <div
                                            className={classNames(styles.wrappeImage, {
                                                [styles.center]: item.imagePosition === IMAGE_POSITION.Center,
                                                [styles.bottom]: item.imagePosition === IMAGE_POSITION.Bottom,
                                            })}
                                        >
                                            <img
                                                className={styles.image}
                                                src={item.image}
                                                alt=""
                                                style={{
                                                    maxWidth: isMobile ? `${item.maxWidthImageMobile}px` : `${item.maxWidthImage}px`,
                                                    height: isMobile ? `${item.heightImageMobile}%` : 'auto',
                                                }}
                                            />
                                        </div>

                                        <div className={styles.wrapperText}>
                                            <div
                                                className={styles.title}
                                                dangerouslySetInnerHTML={{ __html: item.title }}
                                                style={{ color: colorText, maxWidth: item.maxWidthTitle }}
                                            ></div>
                                            <div className={styles.description} style={{ color: colorText }}>
                                                {item.description}
                                            </div>
                                            {item.ctaText && item.ctaUrl ? (
                                                <BusinessButton
                                                    className={styles.learnMore}
                                                    link={item.ctaUrl}
                                                    buttonText={item.ctaText}
                                                    autoMargin={false}
                                                    color={COLORS.WHITE}
                                                    target={item.openLinkInNewTab ? '_blank' : '_self'}
                                                ></BusinessButton>
                                            ) : null}
                                        </div>
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            </div>
        </div>
    );
};

export default BusinessHubCarouselProduct;
